/**
 * bb-legal.js — Legal Trust Center behaviour. Policy pages only.
 *
 * THE POLICY MUST READ FINE WITH THIS FILE ABSENT. Everything here is additive:
 * the table of contents is built from headings that already exist, and if this
 * script never runs the page is still a complete, readable legal document with
 * a hero and a help card. Nothing is hidden pending JavaScript.
 *
 * NO POLICY TEXT LIVES HERE. Headings are read from the DOM Shopify rendered;
 * none of the legal wording is duplicated into this file, so it cannot drift
 * from the policy the way a hand-maintained section list would.
 *
 * Also deliberately absent: any analytics, any network call, any third-party
 * library. A privacy page that phoned home would be its own punchline.
 */
(function () {
  'use strict';

  if (window.__bbLegalInit) return;          /* install once */
  window.__bbLegalInit = true;

  var root = document.querySelector('[data-bb-legal]');
  if (!root) return;

  var body = root.querySelector('.shopify-policy__body');
  if (!body) return;

  var desktopMount = root.querySelector('[data-bb-legal-toc]');
  /* The mobile index is INSERTED after Shopify's <h1> rather than mounted from
     Liquid. Shopify renders the title and body as one atomic block, so there is
     no Liquid seam between them — but this element only ever exists when JS
     runs, so placing it from JS costs nothing in the no-JS case. */
  var policyTitle = root.querySelector('.shopify-policy__title');

  /* ── keep anchors clear of the sticky header ───────────────────────────
     Measured rather than hard-coded: this theme's header height varies by
     breakpoint and by whether the announcement bar is showing. */
  function syncOffsets() {
    var offset = 24;
    try {
      var candidates = document.querySelectorAll(
        'header, .header-wrapper, [class*="header-sticky"], sticky-header'
      );
      for (var i = 0; i < candidates.length; i++) {
        var el = candidates[i];
        var pos = window.getComputedStyle(el).position;
        if (pos !== 'fixed' && pos !== 'sticky') continue;
        var h = el.getBoundingClientRect().height;
        if (h > offset && h < 220) offset = h;
      }
    } catch (e) { /* a measurement failure must not break the page */ }
    var total = Math.round(offset + 20);
    root.style.setProperty('--bbl-scroll-offset', total + 'px');
    root.style.setProperty('--bbl-toc-top', total + 'px');
    return total;
  }
  var scrollOffset = syncOffsets();

  /* ── build the index from the REAL headings ────────────────────────────── */

  function slugify(text, used) {
    var base = String(text || '')
      .toLowerCase()
      .replace(/[‘’“”]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'section';
    /* Shopify policies repeat words like "Contact"; a duplicate id would make
       one anchor unreachable, so collisions get a numeric suffix. */
    var id = base, n = 2;
    while (used[id] || document.getElementById(id)) { id = base + '-' + n; n++; }
    used[id] = true;
    return id;
  }

  var headings = Array.prototype.slice.call(body.querySelectorAll('h2, h3'))
    .filter(function (h) { return (h.textContent || '').trim().length > 1; });

  /* A policy with no headings gets NO INDEX — but the rest of this file still
     has to run. An early `return` here meant the privacy-choices button was
     never revealed on Terms, Refund, Shipping or the opt-out page, all of which
     lack headings. Skip only the index. */
  var hasIndex = headings.length > 0;

  var used = {};
  var entries = headings.map(function (h) {
    if (!h.id) h.id = slugify(h.textContent, used); else used[h.id] = true;
    return { id: h.id, text: (h.textContent || '').trim(), level: h.tagName === 'H3' ? 3 : 2, el: h };
  });

  function buildList(className) {
    var ul = document.createElement('ul');
    ul.className = 'bb-legal__toc-list';
    entries.forEach(function (e) {
      var li = document.createElement('li');
      var a = document.createElement('a');
      a.className = 'bb-legal__toc-link' + (e.level === 3 ? ' bb-legal__toc-link--sub' : '');
      a.href = '#' + e.id;
      a.textContent = e.text;           /* textContent, never innerHTML */
      a.setAttribute('data-bb-toc-for', e.id);
      li.appendChild(a);
      ul.appendChild(li);
    });
    if (className) ul.className += ' ' + className;
    return ul;
  }

  if (hasIndex && desktopMount) {
    var title = document.createElement('p');
    title.className = 'bb-legal__toc-title';
    title.id = 'bb-legal-toc-title';
    title.textContent = 'On this page';
    var nav = document.createElement('nav');
    nav.setAttribute('aria-labelledby', 'bb-legal-toc-title');
    nav.appendChild(buildList());
    desktopMount.appendChild(title);
    desktopMount.appendChild(nav);
    /* Opt the layout into two columns only now that an index really exists.
       Without this the body would be dropped into the empty index column. */
    var mainEl = root.querySelector('.bb-legal__main');
    if (mainEl) mainEl.classList.add('bb-legal__main--indexed');
  }

  if (hasIndex && policyTitle && policyTitle.parentNode) {
    var details = document.createElement('details');
    details.className = 'bb-legal__toc-mobile';
    var summary = document.createElement('summary');
    summary.className = 'bb-legal__toc-summary';
    summary.textContent = 'On this page';
    var mnav = document.createElement('nav');
    mnav.setAttribute('aria-label', 'Policy sections');
    mnav.appendChild(buildList());
    details.appendChild(summary);
    details.appendChild(mnav);
    policyTitle.parentNode.insertBefore(details, policyTitle.nextSibling);
    /* Tapping a section should not leave the index covering the answer. */
    mnav.addEventListener('click', function (ev) {
      if (ev.target && ev.target.closest('a')) details.open = false;
    });
  }

  /* ── scrolling ─────────────────────────────────────────────────────────
     The href stays a real "#id", so with JS broken the browser still jumps
     correctly. This only smooths it and, importantly, MOVES FOCUS — a jump
     that does not move focus strands keyboard and screen-reader users at the
     top of the document. */

  var reduceMotion = false;
  try {
    reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) { /* treat as no preference */ }

  root.addEventListener('click', function (ev) {
    var link = ev.target && ev.target.closest ? ev.target.closest('.bb-legal__toc-link') : null;
    if (!link) return;
    var id = link.getAttribute('data-bb-toc-for');
    var target = id && document.getElementById(id);
    if (!target) return;                 /* let the browser handle it */

    ev.preventDefault();
    var top = target.getBoundingClientRect().top + window.pageYOffset - scrollOffset;
    try {
      window.scrollTo({ top: top, behavior: reduceMotion ? 'auto' : 'smooth' });
    } catch (e) {
      window.scrollTo(0, top);
    }

    /* Focus the heading so the next Tab continues from here. tabindex="-1"
       makes it programmatically focusable without adding it to the tab order. */
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }

    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, '', '#' + id);
    }
  });

  /* ── active section highlight ──────────────────────────────────────────── */

  var links = {};
  Array.prototype.forEach.call(root.querySelectorAll('.bb-legal__toc-link'), function (a) {
    var k = a.getAttribute('data-bb-toc-for');
    (links[k] = links[k] || []).push(a);
  });

  function setCurrent(id) {
    Object.keys(links).forEach(function (k) {
      links[k].forEach(function (a) {
        if (k === id) a.setAttribute('aria-current', 'true');
        else a.removeAttribute('aria-current');
      });
    });
  }

  if (hasIndex && 'IntersectionObserver' in window) {
    var visible = {};
    var io = new IntersectionObserver(function (records) {
      records.forEach(function (r) { visible[r.target.id] = r.isIntersecting; });
      for (var i = 0; i < entries.length; i++) {
        if (visible[entries[i].id]) { setCurrent(entries[i].id); return; }
      }
    }, { rootMargin: '-' + scrollOffset + 'px 0px -68% 0px', threshold: 0 });
    entries.forEach(function (e) { io.observe(e.el); });
  }

  /* ── privacy choices ───────────────────────────────────────────────────
     Shown ONLY if Shopify's banner API is really present. A "Privacy choices"
     control that does nothing is worse than no control at all, so the button
     ships hidden and is revealed on proof of life.

     The API has to be loaded before it exists — its absence at page load says
     nothing, which is the mistake an earlier audit in this project made. */

  var choicesBtn = root.querySelector('[data-bb-privacy-choices]');

  function revealChoices() {
    if (!choicesBtn) return false;
    var api = window.privacyBanner;
    if (!api || typeof api.showPreferences !== 'function') return false;
    choicesBtn.hidden = false;
    return true;
  }

  if (choicesBtn) {
    choicesBtn.addEventListener('click', function () {
      try {
        var p = window.privacyBanner.showPreferences();
        if (p && typeof p.catch === 'function') p.catch(function () {});
      } catch (e) { /* never throw out of a click handler on a legal page */ }
    });

    if (!revealChoices()) {
      try {
        if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
          window.Shopify.loadFeatures(
            [{ name: 'consent-tracking-api', version: '0.1' }],
            function () { revealChoices(); }
          );
        }
      } catch (e) { /* stays hidden, which is the safe outcome */ }
      /* The banner script can arrive after the consent API; check a few times
         and then stop rather than polling forever. */
      var tries = 0;
      var timer = window.setInterval(function () {
        tries++;
        if (revealChoices() || tries > 10) window.clearInterval(timer);
      }, 400);
    }
  }

  /* Header height changes on resize and when the announcement bar closes. */
  var resizeTimer = null;
  window.addEventListener('resize', function () {
    if (resizeTimer) window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(function () { scrollOffset = syncOffsets(); }, 200);
  });
})();
